import React, { useState, useEffect, useMemo } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { toast } from "react-toastify";
import UseComboPackHook from "../../../hooks/UseComboPackHook";
import { useSelector } from "react-redux";

const ComboModal = ({ isOpen, onClose, combo, mode = "add", onSave }) => {
  const { product } = useSelector((state) => state.product);
  const { createComboPack, updateComboPack } = UseComboPackHook();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    linkedServices: [], // [{ serviceId, remainingVisits }]
    totalVisits: "",
    price: "",
    validityDays: "365",
    status: "active",
    _id: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectAll, setSelectAll] = useState(false);

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // 🔍 Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!search.trim()) return product || [];
    return product.filter((p) =>
      p.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, product]);

  // 🧠 Prefill when editing or duplicating combo
  useEffect(() => {
    if (combo && (mode === "edit" || mode === "duplicate")) {
      // Normalize linked services from combo
      const linked =
        combo.linkedService?.map((s) => ({
          serviceId: s.serviceId?._id || s.serviceId,
          remainingVisits: s.remainingVisits || 0,
        })) ||
        combo.linkedServices?.map((s) => ({
          serviceId: s.serviceId?._id || s.serviceId,
          remainingVisits: s.remainingVisits || 0,
        })) ||
        [];

      setFormData({
        name: combo.name || "",
        description: combo.description || "",
        linkedServices: linked,
        totalVisits: combo.totalVisits?.toString() || "",
        price: combo.price?.toString() || "",
        validityDays: combo.validityDays?.toString() || "365",
        status: combo.status || "active",
        _id: combo._id || "",
      });
    } else {
      setFormData({
        name: "",
        description: "",
        linkedServices: [],
        totalVisits: "",
        price: "",
        validityDays: "365",
        status: "active",
      });
    }
    setErrors({});
  }, [combo, mode, isOpen]);

  // 🧩 Form updates
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleProductToggle = (id) => {
    setFormData((prev) => {
      const exists = prev.linkedServices.some((s) => s.serviceId === id);
      const updated = exists
        ? prev.linkedServices.filter((s) => s.serviceId !== id)
        : [...prev.linkedServices, { serviceId: id, remainingVisits: 1 }];
      return { ...prev, linkedServices: updated };
    });
  };

  const handleRemainingChange = (id, value) => {
    setFormData((prev) => ({
      ...prev,
      linkedServices: prev.linkedServices.map((s) =>
        s.serviceId === id ? { ...s, remainingVisits: Number(value) } : s
      ),
    }));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setFormData((prev) => ({ ...prev, linkedServices: [] }));
    } else {
      const all = filteredProducts.map((p) => ({
        serviceId: p._id,
        remainingVisits: 1,
      }));
      setFormData((prev) => ({ ...prev, linkedServices: all }));
    }
    setSelectAll(!selectAll);
  };

  // ✅ Validation
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.price || parseFloat(formData.price) <= 0)
      newErrors.price = "Price must be greater than 0";
    if (!formData.totalVisits || parseInt(formData.totalVisits) <= 0)
      newErrors.totalVisits = "Total visits must be greater than 0";
    if (!formData.validityDays || parseInt(formData.validityDays) <= 0)
      newErrors.validityDays = "Validity days must be greater than 0";
    if (formData.linkedServices.length === 0)
      newErrors.linkedServices = "Select at least one product";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🧾 Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description,
        linkedService: formData.linkedServices.map((ls) => ({
          serviceId: ls.serviceId,
          remainingVisits: ls.remainingVisits || 0,
        })),
        totalVisits: parseInt(formData.totalVisits),
        price: parseFloat(formData.price),
        validityDays: parseInt(formData.validityDays),
        status: formData.status,
      };

      if (mode === "add") await createComboPack(payload);
      else if (mode === "edit") await updateComboPack(formData._id, payload);

      // toast.success(
      //   mode === "add"
      //     ? "Combo pack created successfully"
      //     : "Combo pack updated successfully"
      // );

      onSave && onSave(payload);
      onClose();
    } catch (error) {
      console.error("Error saving combo:", error);
      toast.error("Failed to save combo pack");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalTitle =
    mode === "add"
      ? "Add Combo Pack"
      : mode === "edit"
      ? "Edit Combo Pack"
      : "Duplicate Combo Pack";

  return (
    <div className="fixed inset-0 z-1200 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground">
              {modalTitle}
            </h2>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="overflow-y-auto max-h-[calc(90vh-140px)]"
          >
            <div className="p-6 space-y-6">
              <Input
                label="Name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                error={errors.name}
                required
              />

              <Input
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
              />

              {/* Product list with remaining visits */}
              <div>
                <label className="block font-medium mb-2">
                  Select Products with Remaining Visits
                </label>
                <div className="flex justify-between items-center mb-2">
                  <Input
                    placeholder="Search products..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                  >
                    {selectAll ? "Deselect All" : "Select All"}
                  </Button>
                </div>

                <div className="border rounded-md p-2 max-h-48 overflow-y-auto">
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((p) => {
                      const linked = formData.linkedServices.find(
                        (s) => s.serviceId === p._id
                      );
                      return (
                        <div
                          key={p._id}
                          className="flex justify-between items-center py-1"
                        >
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!linked}
                              onChange={() => handleProductToggle(p._id)}
                            />
                            <span>
                              {p.name}{" "}
                              <span className="text-gray-500 text-sm">
                                ₹{p.price}
                              </span>
                            </span>
                          </label>

                          {linked && (
                            <input
                              type="number"
                              className="w-20 border rounded px-2 py-1 text-sm"
                              value={linked.remainingVisits || 1}
                              min={1}
                              onChange={(e) =>
                                handleRemainingChange(
                                  p._id,
                                  e.target.value || 1
                                )
                              }
                            />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-500 text-sm">No products found</p>
                  )}
                </div>

                {errors.linkedServices && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.linkedServices}
                  </p>
                )}
              </div>

              {/* Other combo fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Total Visits"
                  type="number"
                  value={formData.totalVisits}
                  onChange={(e) =>
                    handleInputChange("totalVisits", e.target.value)
                  }
                  error={errors.totalVisits}
                />
                <Input
                  label="Price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange("price", e.target.value)}
                  error={errors.price}
                />
                <Input
                  label="Validity (Days)"
                  type="number"
                  value={formData.validityDays}
                  onChange={(e) =>
                    handleInputChange("validityDays", e.target.value)
                  }
                  error={errors.validityDays}
                />
                <Select
                  label="Status"
                  options={statusOptions}
                  value={formData.status}
                  onChange={(val) => handleInputChange("status", val)}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-border bg-muted/30">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" loading={loading} iconName="Save">
                {mode === "add" ? "Create Combo" : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ComboModal;
